import React, { useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import Input from "../input";
import Icon from "../../assets/icons";
import FIlterSelect from "../filter-select";
import { Plus } from "lucide-react";

interface SalesDetailsProps {
  control?: any;
  setValue?: any;
  product: any;
  initialProducts?: any[];
  onProductsChange?: (products: any[]) => void;
}

interface Product {
  id: number;
  name: string;
  quantity: number;
  price: number;
  _id: string;
}

const AddProducts: React.FC<SalesDetailsProps> = ({ 
  control, 
  product, 
  initialProducts,
  onProductsChange 
}) => {
  // Use initialProducts if provided, otherwise use default
  const [products, setProducts] = useState<Product[]>(
    initialProducts && initialProducts.length > 0
      ? initialProducts
      : [{ id: Date.now(), name: "", price: 0, _id: "", quantity: 0 }]
  );

  // Create a mapping of product IDs to their details for quick lookups
  const productMap = React.useMemo(() => {
    const map: Record<
      string,
      { name: string; price: number; quantity: number }
    > = {};
    if (product && Array.isArray(product)) {
      product.forEach((item: any) => {
        map[item._id] = {
          name: item.name,
          price: item.price || 0,
          quantity: item.quantity || 0,
        };
      });
    }
    return map;
  }, [product]);

  const productOptions =
    product?.map((item: any) => ({
      value: item._id,
      label: item.name,
    })) || [];

  // Effect to sync products state with parent component
  useEffect(() => {
    if (onProductsChange) {
      onProductsChange(products);
    }
  }, [products, onProductsChange]);

  const addProduct = () => {
    const newProducts = [
      ...products,
      { id: Date.now(), name: "", price: 0, _id: "", quantity: 0 },
    ];
    setProducts(newProducts);
  };

  const removeProduct = (itemId: number) => {
    if (products.length > 1) {
      const newProducts = products.filter((product) => product.id !== itemId);
      setProducts(newProducts);
    }
  };

  const updateProductField = (
    itemId: number,
    field: keyof Product,
    value: any
  ) => {
    const newProducts = products.map((item) =>
      item.id === itemId ? { ...item, [field]: field === 'quantity' || field === 'price' ? Number(value) : value } : item
    );
    setProducts(newProducts);
  };

  // Function to handle product selection
  const handleProductSelection = (itemId: number, productId: string) => {
    const selectedProduct = productMap[productId];
    if (selectedProduct) {
      // Update all relevant fields for the product
      const newProducts = products.map((item) =>
        item.id === itemId
          ? {
              ...item,
              _id: productId,
              name: selectedProduct.name,
              price: selectedProduct.price,
              quantity: 1, // Default to 1 for new selections
            }
          : item
      );
      setProducts(newProducts);
    } else {
      // Just update the ID if product details aren't found
      updateProductField(itemId, "_id", productId);
    }
  };

  return (
    <div className="space-y-4">
      {products.map((product, index) => (
        <div key={product.id} className="grid grid-cols-12 gap-4 items-center">
          <FIlterSelect
            name={`product_id_${product.id}`}
            options={productOptions}
            className="w-full col-span-4"
            label="Item Name"
            onChange={(selectedValue) => {
              handleProductSelection(product.id, selectedValue);
            }}
            value={product._id}
          />

          <Input
            value={product.quantity}
            className="w-full col-span-2"
            label={"Quantity"}
            type="number"
            onChange={(e) =>
              updateProductField(product.id, "quantity", e.target.value)
            }
          />

          <Input
            value={product.price}
            className="w-full col-span-2"
            label={"Unit Price"}
            placeholder="Enter price"
            type="number"
            onChange={(e) =>
              updateProductField(product.id, "price", e.target.value)
            }
          />

          <Input
            value={product.price * product.quantity}
            className="w-full col-span-2"
            label={"Sub Total"}
            placeholder="Amount"
            type="number"
            readOnly
          />

          <div className="col-span-2 flex justify-end pt-1">
            {products.length > 1 && (
              <button
                type="button"
                onClick={() => removeProduct(product.id)}
                className="text-red-500 hover:text-red-700"
              >
                <Icon name="trashSimple" />
              </button>
            )}
          </div>
        </div>
      ))}

      <div
        className="flex justify-center bg-[#E8F2E8] rounded-full p-2 cursor-pointer"
        onClick={addProduct}
      >
        <button
          type="button"
          className="flex items-center justify-center gap-2 text-[#047401] font-medium "
        >
          <Plus size={16} />
          Add New Item
        </button>
      </div>
    </div>
  );
};

export default AddProducts;